import { EditOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useState } from 'react';
import { IBaseSectionType } from '../EstimateForm/types';
import ItemSheet from './ItemSheet';
import { IFormItem } from '~/store/slices/formSlice';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import Button from '~/components/Button';
import { fmPrice } from '~/utils/amount';
import { getAmount } from '../EstimateForm/helpers';
import AddIcon from '~/icons/AddIcon';

const Wrapper = styled.div``;

const Item = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`;

const EditWrapper = styled.div`
  min-width: 30px;
  max-width: 30px;
  min-height: 30px;
  max-height: 30px;
  border-radius: 50%;
  margin-left: 8px;
  background-color: var(--neut7);
`;

const ItemFormSection = ({ formData, setValue }: IBaseSectionType) => {
  const [isOpen, setOpen] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);

  function onAddItem(newItem: IFormItem) {
    const formItems = [...formData.items];
    formItems.push(newItem);
    setValue({ items: formItems });
  }

  function onRemoveItem() {
    const formItems = formData.items.filter((_, index) => index !== editIdx);
    setValue({ items: formItems });
    setOpen(false);
    setEditIdx(null);
  }

  function onEditItem(itemIdx: number, itemData: Partial<IFormItem>) {
    const formItems = [...formData.items];
    const formItem = { ...formItems[itemIdx], ...itemData };
    formItems[itemIdx] = { ...formItem };
    setValue({ items: formItems });
  }

  function onSave(formItemData: Partial<IFormItem>) {
    if (editIdx !== null) {
      onEditItem(editIdx, formItemData);
    } else {
      onAddItem(formItemData as IFormItem);
    }
    setOpen(false);
    setEditIdx(null);
  }

  return (
    <Wrapper>
      <ItemSheet
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        header={editIdx === null ? 'Add line item' : 'Edit line item'}
        data={editIdx === null ? {} : { ...formData.items[editIdx] }}
        currency={formData.currency}
        onSave={onSave}
        editIdx={editIdx}
        onRemoveItem={onRemoveItem}
      />

      <Text size="ssmd">Line items</Text>
      <Gap t={16} />

      <div>
        {formData.items.map((item, index) => {
          return (
            <Item key={index}>
              <div>
                <Text size="xs">{item.description || 'N/A'}</Text>
                <Gap t={2} />
                <Text size="xxs" variant="neutral">
                  {item.quantity} X {fmPrice(formData.currency, item.price)}
                </Text>
              </div>

              <div
                className="center"
                onClick={() => {
                  setEditIdx(index);
                  setOpen(true);
                }}
              >
                <Text size="xs">{fmPrice(formData.currency, getAmount(item))}</Text>
                <Gap r={2} />
                <EditWrapper className="center">
                  <EditOutlined style={{ fontSize: '20px' }} />
                </EditWrapper>
              </div>
            </Item>
          );
        })}
      </div>

      <Button
        className="center full-width"
        variant="secondary"
        onClick={() => {
          setEditIdx(null);
          setOpen(true);
        }}
        size="full"
      >
        <AddIcon />
        <Gap r={4} />
        Add line items
      </Button>
    </Wrapper>
  );
};

export default ItemFormSection;
